/**
 * /learn/:type/:subject/:topic  — universal Focus-Mode learning task page.
 *
 * The 16-week plan emits free-form topics ("Coding", "Number & Place Value",
 * "Algebra: Linear Equations", …). Older code tried to navigate straight to
 * a hand-curated /resources/maths/<cat>/<topic> URL which obviously fails
 * for new or VR/NVR topics. This page closes that gap with a 3-step flow:
 *
 *   1. Concept — short AI-generated explanation (or fallback intro)
 *   2. Worked Example — model question + step-by-step solution
 *   3. Quick Practice — 5-question mini quiz, immediate feedback, XP reward
 *
 * Works the same across 7+, 11+, Year 9 and GCSE because the AI tutor and the
 * back-end question banks are already age-aware via the student's profile.
 */

import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import FocusMode from '@/components/focus/FocusMode';
import FocusCelebration from '@/components/focus/FocusCelebration';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Lightbulb, CheckCircle2, XCircle, Sparkles, ChevronRight, Loader2, Zap, BookOpen, Trophy } from 'lucide-react';
import { resolveStageSlug } from '@/utils/learningPathRouter';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Step labels for the FocusMode top bar.
const STEP_LABELS = ['Quick Recap', 'Worked Example', 'Try It', 'Exam-Style Question', 'Wrap-up'];

// Age-group flavour: each stage gets its own headline copy + accent. Same
// canonical layout — just dressed appropriately so a 7-year-old gets a warm
// mascot greeting while a GCSE student sees exam-board framing.
const STAGE_FLAVOUR = {
  '7-plus': {
    mascot: '🦉',
    greeting: (topic) => `Hi there! Let's explore ${topic} together.`,
    recapLabel: 'What you need to know',
    quizLabel: 'Your Turn!',
    examLabel: 'Mini Challenge',
    wrapEmoji: '🎉',
    accent: 'text-emerald-700 dark:text-emerald-300',
  },
  '11-plus': {
    mascot: '✨',
    greeting: (topic) => `Today's topic: ${topic}.`,
    recapLabel: 'Quick Recap',
    quizLabel: 'Try It',
    examLabel: 'Exam-Style Question',
    wrapEmoji: '🏆',
    accent: 'text-purple-700 dark:text-purple-300',
  },
  'year-9': {
    mascot: '📘',
    greeting: (topic) => `KS3 focus: ${topic}.`,
    recapLabel: 'Concept Recap',
    quizLabel: 'Check Yourself',
    examLabel: 'Exam-Style Question',
    wrapEmoji: '🎯',
    accent: 'text-sky-700 dark:text-sky-300',
  },
  'gcse': {
    mascot: '🎓',
    greeting: (topic) => `GCSE topic: ${topic}.`,
    recapLabel: 'Specification Recap',
    quizLabel: 'Knowledge Check',
    examLabel: 'Past-Paper Style Question',
    wrapEmoji: '🏆',
    accent: 'text-indigo-700 dark:text-indigo-300',
  },
};

const LearningTask = () => {
  const { type = 'revision', subject = 'maths', topic = 'general' } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Adaptive difficulty injected by /api/learning-path/today (foundation,
  // secure, greater_depth, exam_ready, stretch). Falls back to a sensible
  // default if the URL was opened directly.
  const difficulty = (searchParams.get('difficulty') || 'greater_depth').toLowerCase();

  const currentUser = (() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
  })();
  const stage = resolveStageSlug(currentUser);

  // Pretty topic label for display (kebab → Title Case).
  const topicLabel = topic.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const subjectLabel = subject.replace(/-/g, ' ');

  // Stash for the Home Dashboard "Continue Learning" hero card.
  useEffect(() => {
    try {
      sessionStorage.setItem('edpies.activeLearningTask', JSON.stringify({
        topic_slug: topic,
        topic_label: topicLabel,
        subject,
        subject_label: subjectLabel,
        task_type: type,
        difficulty,
        deep_link: `/learn/${type}/${subject}/${topic}?difficulty=${difficulty}`,
        started_at: new Date().toISOString(),
      }));
    } catch { /* sessionStorage unavailable — non-fatal */ }
  }, [type, subject, topic, topicLabel, subjectLabel, difficulty]);

  const [stepIdx, setStepIdx] = useState(0);
  const [concept, setConcept] = useState(null); // { explanation, key_points[] }
  const [example, setExample] = useState(null); // { question, answer, steps[] }
  const [quiz, setQuiz] = useState([]);         // [{ question, options, correct_answer, explanation }]
  const [examQuestion, setExamQuestion] = useState(null); // single harder exam-style question
  const [examAnswer, setExamAnswer] = useState(null);     // user's choice
  const [answers, setAnswers] = useState({});
  const [loadingStep, setLoadingStep] = useState(true);
  const [completed, setCompleted] = useState(false);

  const flavour = STAGE_FLAVOUR[stage] || STAGE_FLAVOUR['11-plus'];

  // Translate the difficulty tag into a phrase the AI tutor will use to
  // calibrate explanations + worked examples.
  const difficultyPhrase = ({
    foundation: 'pitched at a Foundation level — extra clear, small steps, no jargon',
    secure: 'pitched at a Secure level — confident but still scaffolded',
    greater_depth: 'pitched at Greater Depth — concise with reasoning emphasised',
    exam_ready: 'pitched at Exam-Ready level — exam-style language and timing focus',
    stretch: 'pitched at Stretch / Scholarship level — challenging twist questions and rigorous justification',
  })[difficulty] || 'pitched at Greater Depth — concise with reasoning emphasised';

  // ----------------------------------------------------------------------
  // Content loaders — talk to the same AI tutor endpoint we already ship
  // (/api/ai-tutor/chat) for concept + worked example, and to the practice
  // paper endpoint for the mini quiz so questions match the bank quality.
  // ----------------------------------------------------------------------
  const loadConcept = useCallback(async () => {
    setLoadingStep(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API}/api/ai-tutor/chat`,
        {
          message: `Explain "${topicLabel}" for a ${stage} student, ${difficultyPhrase}, in 4 short sentences. Then list 3 key points as bullet items prefixed with "•".`,
          context: { subject: subjectLabel, topic: topicLabel, stage, difficulty },
        },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      const reply = data?.reply || data?.message || '';
      const [explainPart, ...rest] = reply.split(/\n+/);
      const bullets = rest.filter((l) => l.trim().startsWith('•')).map((l) => l.replace(/^•\s*/, ''));
      setConcept({
        explanation: explainPart || `Let's explore ${topicLabel}.`,
        key_points: bullets.length ? bullets : [
          `Understand the core idea of ${topicLabel}.`,
          'Practise a couple of examples before tackling new ones.',
          'Watch out for common mistakes — they reveal what you really know.',
        ],
      });
    } catch {
      setConcept({
        explanation: `${topicLabel} is one of the key topics in your ${subjectLabel} learning plan. Read the points below to refresh the core ideas before practising.`,
        key_points: [
          `Recall the definition and core rules of ${topicLabel}.`,
          'Try a worked example to see the steps in action.',
          'Then attempt the quick quiz to lock it in.',
        ],
      });
    } finally {
      setLoadingStep(false);
    }
  }, [topicLabel, subjectLabel, stage, difficulty, difficultyPhrase]);

  const loadExample = useCallback(async () => {
    setLoadingStep(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API}/api/ai-tutor/chat`,
        {
          message: `Give one worked example question for "${topicLabel}" suitable for a ${stage} student, ${difficultyPhrase}. Format strictly as:\nQ: <question>\nA: <final answer>\nSTEPS: <step 1> | <step 2> | <step 3>`,
          context: { subject: subjectLabel, topic: topicLabel, stage, difficulty },
        },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      const reply = data?.reply || '';
      const q = (reply.match(/Q:\s*([^\n]+)/) || [])[1] || `Try this ${topicLabel} question.`;
      const a = (reply.match(/A:\s*([^\n]+)/) || [])[1] || '';
      const steps = ((reply.match(/STEPS:\s*([^\n]+)/) || [])[1] || '').split('|').map((s) => s.trim()).filter(Boolean);
      setExample({ question: q, answer: a, steps });
    } catch {
      setExample({ question: `Try this ${topicLabel} question.`, answer: '', steps: [] });
    } finally {
      setLoadingStep(false);
    }
  }, [topicLabel, subjectLabel, stage, difficulty, difficultyPhrase]);

  const loadQuiz = useCallback(async () => {
    setLoadingStep(true);
    try {
      const token = localStorage.getItem('token');
      const stageForPaper = stage === '11-plus' ? '11-plus' : stage === '7-plus' ? '7-plus' : stage === 'year-9' ? 'year-9' : 'gcse';
      const sl = subjectLabel.toLowerCase();
      const subjForPaper =
        sl.includes('non') ? 'non-verbal-reasoning' :
        sl.includes('verbal') ? 'verbal-reasoning' :
        sl.includes('eng') ? 'english' :
        sl.includes('biolog') ? 'biology' :
        sl.includes('chem') ? 'chemistry' :
        sl.includes('phys') ? 'physics' :
        sl.includes('science') ? 'science' :
        'maths';
      const { data } = await axios.get(`${API}/api/papers/${stageForPaper}/${subjForPaper}/practice/1`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const all = data?.questions || [];
      setQuiz(all.slice(0, 5));
      // Pick a harder question (look ahead in the bank) as the exam-style one.
      setExamQuestion(all[Math.min(7, all.length - 1)] || null);
    } catch {
      setQuiz([]);
      setExamQuestion(null);
    } finally {
      setLoadingStep(false);
    }
  }, [stage, subjectLabel]);

  // Load content for the current step on demand.
  useEffect(() => {
    if (stepIdx === 0 && !concept) loadConcept();
    else if (stepIdx === 1 && !example) loadExample();
    else if (stepIdx === 2 && quiz.length === 0) loadQuiz();
  }, [stepIdx, concept, example, quiz.length, loadConcept, loadExample, loadQuiz]);

  // Mark the 16-week task complete on backend once Wrap-up step is reached.
  useEffect(() => {
    if (stepIdx !== STEP_LABELS.length - 1 || completed) return;
    const stash = (() => {
      try { return JSON.parse(sessionStorage.getItem('edpies.activeLearningTask') || 'null'); } catch { return null; }
    })();
    const taskId = stash?.id;
    if (!taskId) { setCompleted(true); return; }
    (async () => {
      const reward = ({ revision: 10, practice: 15, quiz: 25 })[type] || 15;
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${API}/api/calendar/toggle-task`,
          { task_id: taskId, completed: true },
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        toast.success(`+${reward} XP — Task complete!`);
      } catch { /* non-fatal */ }
      // Broadcast for the global XP/Level badge to pick up.
      window.dispatchEvent(new CustomEvent('edpies.xpEarned', { detail: { amount: reward } }));
      sessionStorage.removeItem('edpies.activeLearningTask');
      setCompleted(true);
    })();
  }, [stepIdx, completed, type]);

  const correctCount = quiz.reduce((acc, q, i) => acc + (answers[i] === q.correct_answer ? 1 : 0), 0);
  const quizAnsweredAll = quiz.length > 0 && Object.keys(answers).length >= quiz.length;

  const renderStep = () => {
    if (loadingStep) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-purple-500 mb-3" />
          <p className="text-sm opacity-70">Preparing your {STEP_LABELS[stepIdx].toLowerCase()}…</p>
        </div>
      );
    }

    if (stepIdx === 0 && concept) {
      return (
        <div className="space-y-5">
          <div className={`flex items-center gap-2 text-xs uppercase tracking-wider opacity-70 ${flavour.accent}`}>
            <span className="text-base" aria-hidden="true">{flavour.mascot}</span>
            <BookOpen className="h-4 w-4" /> {flavour.recapLabel}
          </div>
          <h2 className="text-2xl md:text-3xl font-bold leading-tight" data-testid="learn-concept-title">{topicLabel}</h2>
          <p className="text-sm opacity-70 italic">{flavour.greeting(topicLabel)}</p>
          <p className="leading-relaxed" data-testid="learn-concept-explanation">{concept.explanation}</p>
          <div className="rounded-xl border border-sky-500/30 bg-sky-500/10 p-4">
            <p className="font-semibold mb-2 flex items-center gap-2 text-sky-700 dark:text-sky-300">
              <Lightbulb className="h-4 w-4" /> Key Points
            </p>
            <ul className="space-y-2 list-disc list-inside">
              {concept.key_points.map((p, i) => <li key={i}>{p}</li>)}
            </ul>
          </div>
        </div>
      );
    }

    if (stepIdx === 1 && example) {
      return (
        <div className="space-y-5">
          <div className={`flex items-center gap-2 text-xs uppercase tracking-wider opacity-70 ${flavour.accent}`}>
            <Sparkles className="h-4 w-4 text-amber-500" /> Worked Example
          </div>
          <p className="font-semibold leading-snug" data-testid="learn-example-q">{example.question}</p>
          {example.answer && (
            <p className="rounded-lg bg-emerald-500/10 border border-emerald-500/40 p-3">
              <span className="font-semibold mr-2 text-emerald-700 dark:text-emerald-300">Answer:</span>
              {example.answer}
            </p>
          )}
          {example.steps.length > 0 && (
            <div>
              <p className="font-semibold mb-2">Step by step</p>
              <ol className="space-y-2 list-decimal list-inside">
                {example.steps.map((s, i) => <li key={i}>{s}</li>)}
              </ol>
            </div>
          )}
        </div>
      );
    }

    if (stepIdx === 2) {
      if (!quiz.length) return <p className="opacity-70">No questions are available for this topic yet — feel free to mark complete.</p>;
      return (
        <div className="space-y-6">
          <div className="flex items-center justify-between text-xs">
            <div className={`flex items-center gap-2 uppercase tracking-wider opacity-70 ${flavour.accent}`}>
              <Zap className="h-4 w-4 text-amber-500" /> {flavour.quizLabel}
            </div>
            <span className="font-semibold">{Object.keys(answers).length} / {quiz.length} answered</span>
          </div>
          {quiz.map((q, i) => {
            const picked = answers[i];
            const hasAnswered = picked !== undefined;
            return (
              <div key={i} className="space-y-3 pb-5 border-b border-current/10 last:border-0">
                <p className="font-semibold">Q{i + 1}. {q.question}</p>
                <RadioGroup
                  value={picked !== undefined ? picked.toString() : ''}
                  onValueChange={(v) => setAnswers((a) => ({ ...a, [i]: parseInt(v, 10) }))}
                  className="space-y-2"
                >
                  {(q.options || []).map((opt, oi) => {
                    const isCorrect = oi === q.correct_answer;
                    const isPicked = picked === oi;
                    return (
                      <label key={oi} htmlFor={`q${i}-${oi}`} className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors focus-within:ring-2 focus-within:ring-purple-500 ${
                        hasAnswered
                          ? isCorrect ? 'bg-emerald-500/10 border-emerald-500'
                          : isPicked ? 'bg-rose-500/10 border-rose-500' : 'border-current/15 opacity-70'
                          : isPicked ? 'bg-purple-500/10 border-purple-500' : 'border-current/15 hover:border-purple-500'
                      }`} data-testid={`learn-quiz-${i}-opt-${oi}`}>
                        <RadioGroupItem value={oi.toString()} id={`q${i}-${oi}`} disabled={hasAnswered} />
                        <span className="flex-1 text-sm">{opt}</span>
                        {hasAnswered && isCorrect && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                        {hasAnswered && isPicked && !isCorrect && <XCircle className="h-4 w-4 text-rose-600" />}
                      </label>
                    );
                  })}
                </RadioGroup>
                {hasAnswered && q.explanation && (
                  <p className="text-sm bg-sky-500/10 border border-sky-500/30 rounded p-3">
                    <span className="font-semibold text-sky-700 dark:text-sky-300">Why:</span> {q.explanation}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    // Step 3 — Exam-Style Question (single, harder, with reveal).
    if (stepIdx === 3) {
      if (!examQuestion) {
        return (
          <div className="space-y-4">
            <p className="opacity-70">No exam-style question available for this topic yet — feel free to wrap up.</p>
          </div>
        );
      }
      const picked = examAnswer;
      const hasAnswered = picked !== null && picked !== undefined;
      return (
        <div className="space-y-5">
          <div className={`flex items-center gap-2 text-xs uppercase tracking-wider opacity-70 ${flavour.accent}`}>
            <Trophy className="h-4 w-4 text-amber-500" /> {flavour.examLabel}
          </div>
          <p className="font-semibold leading-snug" data-testid="learn-exam-question">{examQuestion.question}</p>
          <RadioGroup
            value={picked !== null && picked !== undefined ? picked.toString() : ''}
            onValueChange={(v) => setExamAnswer(parseInt(v, 10))}
            className="space-y-2"
          >
            {(examQuestion.options || []).map((opt, oi) => {
              const isCorrect = oi === examQuestion.correct_answer;
              const isPicked = picked === oi;
              return (
                <label key={oi} htmlFor={`ex-opt-${oi}`} className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors focus-within:ring-2 focus-within:ring-purple-500 ${
                  hasAnswered
                    ? isCorrect ? 'bg-emerald-500/10 border-emerald-500'
                    : isPicked ? 'bg-rose-500/10 border-rose-500' : 'border-current/15 opacity-70'
                    : isPicked ? 'bg-purple-500/10 border-purple-500' : 'border-current/15 hover:border-purple-500'
                }`} data-testid={`learn-exam-opt-${oi}`}>
                  <RadioGroupItem value={oi.toString()} id={`ex-opt-${oi}`} disabled={hasAnswered} />
                  <span className="flex-1 text-sm">{opt}</span>
                  {hasAnswered && isCorrect && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                  {hasAnswered && isPicked && !isCorrect && <XCircle className="h-4 w-4 text-rose-600" />}
                </label>
              );
            })}
          </RadioGroup>
          {hasAnswered && examQuestion.explanation && (
            <p className="text-sm bg-sky-500/10 border border-sky-500/30 rounded p-3">
              <span className="font-semibold text-sky-700 dark:text-sky-300">Mark-scheme insight:</span> {examQuestion.explanation}
            </p>
          )}
        </div>
      );
    }

    // Step 4 — Wrap-up
    const totalEarned = correctCount + (examAnswer !== null && examQuestion && examAnswer === examQuestion.correct_answer ? 1 : 0);
    return (
      <div className="text-center space-y-4 py-6">
        <div className="text-5xl" aria-hidden="true">{flavour.wrapEmoji}</div>
        <h2 className="text-2xl md:text-3xl font-bold" data-testid="learn-wrapup-title">Task Complete!</h2>
        <p className="opacity-80">
          You finished <span className="font-semibold">{topicLabel}</span> · scored
          {' '}<span className={`font-semibold ${flavour.accent}`}>{totalEarned} / {(quiz.length || 0) + (examQuestion ? 1 : 0)}</span> across the practice and exam-style question.
        </p>
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300 font-semibold">
          <Zap className="h-4 w-4" /> +{({ revision: 10, practice: 15, quiz: 25 })[type] || 15} XP earned
        </div>
        <div className="pt-4">
          <Button onClick={() => navigate('/learning-path')} className="bg-purple-600 hover:bg-purple-700 text-white">
            Back to Learning Plan <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  };

  const canAdvance =
    stepIdx === 0 ? !!concept :
    stepIdx === 1 ? !!example :
    stepIdx === 2 ? quizAnsweredAll || quiz.length === 0 :
    stepIdx === 3 ? examAnswer !== null || !examQuestion :
    false;

  return (
    <FocusMode
      title={`${({ revision: 'Revision', practice: 'Practice', quiz: 'Quiz' })[type] || 'Learn'} · ${topicLabel}`}
      current={stepIdx + 1}
      total={STEP_LABELS.length}
      progressLabel={`${STEP_LABELS[stepIdx]} (${stepIdx + 1} of ${STEP_LABELS.length})`}
      onPrev={() => setStepIdx((i) => Math.max(0, i - 1))}
      onNext={() => setStepIdx((i) => Math.min(STEP_LABELS.length - 1, i + 1))}
      canPrev={stepIdx > 0}
      canNext={canAdvance && stepIdx < STEP_LABELS.length - 1}
      onExit={() => navigate(-1)}
      showEstimate={false}
    >
      <FocusCelebration correctCount={correctCount} />
      {renderStep()}
    </FocusMode>
  );
};

export default LearningTask;
